import * as React from "react";
import { Eye, EyeOff, Copy, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { analyzePasswordStrength } from "@/utils/securityUtils";

export interface PasswordInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  showStrengthMeter?: boolean;
  showGenerator?: boolean;
  personalInfo?: { name?: string; email?: string; phone?: string; company?: string; };
  onStrengthChange?: (analysis: ReturnType<typeof analyzePasswordStrength>) => void;
}

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ 
    className, 
    showStrengthMeter = false, 
    showGenerator = false,
    personalInfo = {},
    onStrengthChange,
    value,
    onChange,
    ...props 
  }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const [analysis, setAnalysis] = React.useState<ReturnType<typeof analyzePasswordStrength> | null>(null);

    // Analyze password strength when value changes
    React.useEffect(() => {
      if (showStrengthMeter && value && typeof value === 'string') {
        const newAnalysis = analyzePasswordStrength(value, personalInfo);
        setAnalysis(newAnalysis);
        onStrengthChange?.(newAnalysis);
      } else if (!value) {
        setAnalysis(null);
        onStrengthChange?.(null as any);
      }
    }, [value, personalInfo, showStrengthMeter, onStrengthChange]);

    const generateSecurePassword = () => {
      const lowercase = 'abcdefghijklmnopqrstuvwxyz';
      const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const numbers = '0123456789';
      const symbols = '!@#$%^&*(),.?":{}|<>';
      
      let password = '';
      const length = 14;
      
      // Ensure at least one character from each category
      password += lowercase[Math.floor(Math.random() * lowercase.length)];
      password += uppercase[Math.floor(Math.random() * uppercase.length)];
      password += numbers[Math.floor(Math.random() * numbers.length)];
      password += symbols[Math.floor(Math.random() * symbols.length)];
      
      // Fill the rest randomly
      const allChars = lowercase + uppercase + numbers + symbols;
      for (let i = 4; i < length; i++) {
        password += allChars[Math.floor(Math.random() * allChars.length)];
      }
      
      // Shuffle the password
      password = password.split('').sort(() => Math.random() - 0.5).join('');
      
      // Update the input value
      const event = {
        target: { value: password }
      } as React.ChangeEvent<HTMLInputElement>;
      onChange?.(event);
    };

    const copyToClipboard = async () => {
      if (value && typeof value === 'string') {
        await navigator.clipboard.writeText(value);
      }
    };

    const getStrengthColor = (level: string) => {
      switch (level) {
        case 'excellent': return 'hsl(var(--chart-2))'; // Green
        case 'strong': return 'hsl(var(--chart-3))'; // Light green
        case 'good': return 'hsl(var(--chart-4))'; // Yellow
        case 'fair': return 'hsl(var(--chart-5))'; // Orange
        case 'weak': return 'hsl(var(--destructive))'; // Red
        default: return 'hsl(var(--muted))';
      }
    };

    const getStrengthText = (level: string) => {
      switch (level) {
        case 'excellent': return 'Excellent';
        case 'strong': return 'Strong';
        case 'good': return 'Good';
        case 'fair': return 'Fair';
        case 'weak': return 'Weak';
        default: return '';
      }
    };

    return (
      <div className="space-y-2">
        <div className="relative">
          <Input
            type={showPassword ? "text" : "password"}
            className={cn("pr-20", className)}
            ref={ref}
            value={value}
            onChange={onChange}
            {...props}
          />
          <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
            {showGenerator && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={generateSecurePassword}
                    >
                      <RefreshCw className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Generate secure password</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {value && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={copyToClipboard}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Copy password</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-3 w-3" />
              ) : (
                <Eye className="h-3 w-3" />
              )}
              <span className="sr-only">
                {showPassword ? "Hide password" : "Show password"}
              </span>
            </Button>
          </div>
        </div>
        
        {showStrengthMeter && analysis && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Password strength:</span>
              <span 
                className="font-medium"
                style={{ color: getStrengthColor(analysis.level) }}
              >
                {getStrengthText(analysis.level)} ({analysis.score}/100)
              </span>
            </div>
            <Progress 
              value={analysis.score} 
              className="h-2"
              style={{
                '--progress-foreground': getStrengthColor(analysis.level)
              } as React.CSSProperties}
            />
            {analysis.errors.length > 0 && (
              <ul className="text-sm text-destructive space-y-1">
                {analysis.errors.map((error, index) => (
                  <li key={index} className="flex items-start space-x-1">
                    <span className="text-xs mt-0.5">•</span>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            )}
            {analysis.suggestions.length > 0 && analysis.errors.length === 0 && (
              <ul className="text-sm text-muted-foreground space-y-1">
                {analysis.suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start space-x-1">
                    <span className="text-xs mt-0.5">💡</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  }
);

PasswordInput.displayName = "PasswordInput";

export { PasswordInput };